import axios from "axios";
import { ATTACHMENTS_PRESIGN_API_URL, REQUEST_ATTACHMENT_CONFIRM_API_URL } from "../config/api";

export function uploadExpiresAt(duration, now = Date.now()) {
    const match = /^(\d+(?:\.\d+)?)\s*(ms|s|m|h|d)$/.exec(String(duration));
    if (!match) return null;
    return now + Number(match[1]) * { ms: 1, s: 1000, m: 60000, h: 3600000, d: 86400000 }[match[2]];
}

export function attachmentErrorDetails(error) {
    const rawStatus = error?.status ?? error?.response?.status;
    const status = Number.isFinite(Number(rawStatus)) ? Number(rawStatus) : null;

    if (status === 409) {
        return {
            status,
            retryable: true,
            message: "O arquivo enviado diverge do arquivo informado. Tente anexá-lo novamente.",
        };
    }
    if (status === 410) {
        return {
            status,
            retryable: true,
            message: "A autorização de upload expirou. Tente novamente para gerar uma nova autorização.",
        };
    }
    if (status === 429) {
        return {
            status,
            retryable: false,
            message: "O limite de tentativas para anexar fotos foi atingido.",
        };
    }

    return { status, retryable: true, message: "" };
}

function storageUploadError(response) {
    const error = new Error("Falha ao enviar foto ao armazenamento.");
    error.status = response.status;
    return error;
}

// Keep progress in memory so retries skip confirmed files and resume confirmation.
export async function persistirAnexo({ requestId, uploadToken, file, progress }) {
    if (progress.confirmed) return;
    if (!requestId || !uploadToken) throw new Error("Resposta sem dados para anexar fotos.");
    const config = {
        headers: { Authorization: `Bearer ${uploadToken}`, "Content-Type": "application/json" },
        withCredentials: false,
    };
    const metadata = {
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        filename: file.name || "foto.jpg",
    };
    if (!progress.checksum) {
        const hash = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
        progress.checksum = Array.from(new Uint8Array(hash))
            .map((byte) => byte.toString(16).padStart(2, "0")).join("");
    }
    if (!progress.uploaded) {
        const { data: presign } = await axios.post(
            ATTACHMENTS_PRESIGN_API_URL(requestId), metadata, config
        );
        const hasValidHeaders = presign?.headers && typeof presign.headers === "object" &&
            !Array.isArray(presign.headers);
        if (!presign?.uploadId || !presign.url || !presign.method || !hasValidHeaders) {
            throw new Error("Resposta de presign incompleta.");
        }
        // Fetch is isolated from API authorization defaults and omits all cookies.
        const response = await fetch(presign.url, {
            method: presign.method,
            headers: presign.headers,
            body: file,
            credentials: "omit",
        });
        if (!response.ok) throw storageUploadError(response);
        progress.presign = presign;
        progress.uploaded = true;
    }
    try {
        await axios.post(REQUEST_ATTACHMENT_CONFIRM_API_URL(requestId), {
            uploadId: progress.presign.uploadId,
            checksumSha256: progress.checksum,
        }, config);
    } catch (error) {
        const { status } = attachmentErrorDetails(error);
        if ([409, 410].includes(status)) {
            // A divergent file or an expired presign needs a fresh presign and PUT.
            progress.presign = null;
            progress.uploaded = false;
        }
        throw error;
    }
    progress.confirmed = true;
}
